/**
 * MASTER_PLAN.md §3.9 work item #14 — minimal CSV parser for the
 * maintenance import flow. No `papaparse`/similar dependency added:
 * the shape here is simple (School ID, Address, City, State, Zip — no
 * embedded newlines expected) and Session 39's own note about avoiding
 * unverified package.json changes applies just as much to the frontend.
 * Handles quoted fields and doubled-quote escaping defensively anyway,
 * since real-world CSV exports (Excel, Google Sheets) commonly quote
 * fields containing commas.
 */

export interface ParsedCsv {
  headers: string[];
  rows: string[][];
}

export function parseCsv(text: string): ParsedCsv {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  let sawAnyField = false;

  const pushField = () => {
    row.push(field);
    field = "";
  };
  const pushRow = () => {
    pushField();
    rows.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      sawAnyField = true;
    } else if (ch === ",") {
      pushField();
      sawAnyField = true;
    } else if (ch === "\n") {
      pushRow();
      sawAnyField = false;
    } else if (ch === "\r") {
      // skip — \n handles the row break
    } else {
      field += ch;
      sawAnyField = true;
    }
  }
  if (sawAnyField || field.length > 0 || row.length > 0) {
    pushRow();
  }

  const nonEmpty = rows.filter((r) => r.some((cell) => cell.trim() !== ""));
  const [headers, ...dataRows] = nonEmpty;
  return { headers: (headers || []).map((h) => h.trim()), rows: dataRows };
}

/**
 * Maps CSV headers to the fields the import flow needs, tolerant of
 * casing/spacing (e.g. "School ID" / "school_id" / "SchoolID"). Returns
 * null for a field if no header matches — caller surfaces that as a
 * file-level error rather than guessing.
 */
export function mapCsvColumns(headers: string[]) {
  const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
  const normalized = headers.map(normalize);

  const find = (...candidates: string[]) => {
    for (const c of candidates) {
      const idx = normalized.indexOf(normalize(c));
      if (idx >= 0) return idx;
    }
    return -1;
  };

  return {
    externalId: find("schoolid", "school id", "id"),
    // The downloadable template has always advertised a "Location Name"
    // column, but nothing mapped it, so every imported site was named
    // after its street address. For a 1,084-row LCG portfolio that means
    // losing "The Goddard School #4127" in favour of "3125 Old Milton
    // Pkwy" on every roster row. Mapped here; the commit path uses it
    // and falls back to the address when the column is absent.
    name: find(
      "locationname",
      "location name",
      "sitename",
      "site name",
      "name",
      "schoolname",
      "school name",
    ),
    line1: find("address", "line1", "street", "streetaddress"),
    city: find("city"),
    state: find("state"),
    zip: find("zip", "zipcode", "postalcode", "postal code"),
  };
}
